import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Package, Plus, Edit, Trash2, Search, DollarSign,
  Stethoscope, FlaskConical, Heart, Scissors, Check, X, Building2, Link
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'consultation', label: 'Consultation', icon: Stethoscope, color: 'text-blue-600 bg-blue-50' },
  { value: 'lab_test', label: 'Lab / Test', icon: FlaskConical, color: 'text-purple-600 bg-purple-50' },
  { value: 'nursing', label: 'Nursing', icon: Heart, color: 'text-pink-600 bg-pink-50' },
  { value: 'procedure', label: 'Procedure', icon: Scissors, color: 'text-amber-600 bg-amber-50' }
];

const ServiceCatalogue = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Phase 6.4: Insurance mapping state
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingService, setMappingService] = useState(null);
  const [insurerMappings, setInsurerMappings] = useState([]);
  const [savingMappings, setSavingMappings] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'consultation',
    default_price: '',
    billable: true,
    active: true
  });

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services`, { params: { active_only: false } });
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchInsuranceProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/insurance-providers?active_only=true`);
      setInsuranceProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch insurance providers:', error);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchInsuranceProviders();
  }, [fetchServices, fetchInsuranceProviders]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        default_price: parseFloat(formData.default_price)
      };
      
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, payload);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success('Service created');
      }
      
      setShowModal(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      category: service.category,
      default_price: service.default_price.toString(),
      billable: service.billable,
      active: service.active
    });
    setShowModal(true);
  };

  const handleDeactivate = async (service) => {
    if (!window.confirm(`Are you sure you want to deactivate "${service.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/services/${service.id}`);
      toast.success('Service deactivated');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate service');
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      category: 'consultation',
      default_price: '',
      billable: true,
      active: true
    });
  };

  // Phase 6.4: Insurance mapping functions
  const handleOpenMappings = async (service) => {
    setMappingService(service);
    // Initialize mappings for all providers, preserving existing ones
    const existingMappings = service.insurer_mappings || [];
    const allMappings = insuranceProviders.map(provider => {
      const existing = existingMappings.find(m => m.insurer_id === provider.id);
      return existing || {
        insurer_id: provider.id,
        insurer_name: provider.name,
        insurer_short_code: provider.short_code,
        insurer_code: '',
        insurer_description: '',
        insurer_price: service.default_price
      };
    });
    setInsurerMappings(allMappings);
    setShowMappingModal(true);
  };

  const handleMappingChange = (insurerId, field, value) => {
    setInsurerMappings(prev => prev.map(m => 
      m.insurer_id === insurerId ? { ...m, [field]: value } : m
    ));
  };

  const handleSaveMappings = async () => {
    if (!mappingService) return;
    
    setSavingMappings(true);
    try {
      // Only save mappings that have an insurer_code set
      const mappingsToSave = insurerMappings
        .filter(m => m.insurer_code && m.insurer_code.trim())
        .map(m => ({
          insurer_id: m.insurer_id,
          insurer_code: m.insurer_code.trim(),
          insurer_description: m.insurer_description || '',
          insurer_price: parseFloat(m.insurer_price) || mappingService.default_price
        }));
      
      await axios.put(`${API}/services/${mappingService.id}/insurer-mappings`, {
        insurer_mappings: mappingsToSave
      });
      
      toast.success('Insurance mappings saved');
      setShowMappingModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSavingMappings(false);
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || service.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD' }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="service-catalogue-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Service Catalogue</h1>
            <p className="text-slate-600 mt-1">Manage billable services and prices</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-service-btn"
          >
            <Plus className="w-4 h-4" />
            Add Service
          </Button>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="p-4 flex flex-col md:flex-row items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Services List */}
        <div className="card" data-testid="services-list">
          <div className="p-4 border-b-2 border-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Services ({filteredServices.length})
            </h2>
          </div>
          
          {filteredServices.length > 0 ? (
            <div className="divide-y-2 divide-slate-100">
              {filteredServices.map((service) => {
                const categoryInfo = getCategoryInfo(service.category);
                const CategoryIcon = categoryInfo.icon;
                
                return (
                  <div
                    key={service.id}
                    className={`p-4 flex items-center justify-between ${!service.active ? 'opacity-50 bg-slate-50' : 'hover:bg-slate-50'}`}
                    data-testid={`service-row-${service.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryInfo.color}`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{service.name}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-600">
                          <span className="capitalize">{categoryInfo.label}</span>
                          {!service.billable && (
                            <span className="text-amber-600 text-xs">(Non-billable)</span>
                          )}
                          {!service.active && (
                            <span className="text-red-600 text-xs">(Inactive)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-slate-900 text-lg">
                          {formatCurrency(service.default_price)}
                        </p>
                        <div className="flex items-center gap-2 justify-end">
                          {service.billable ? (
                            <span className="text-green-600 text-xs flex items-center gap-1">
                              <Check className="w-3 h-3" /> Billable
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs flex items-center gap-1">
                              <X className="w-3 h-3" /> Not Billable
                            </span>
                          )}
                          {/* Phase 6.4: Show insurer mapping count */}
                          {service.insurer_mappings && service.insurer_mappings.length > 0 && (
                            <span className="text-blue-600 text-xs flex items-center gap-1">
                              <Building2 className="w-3 h-3" /> {service.insurer_mappings.length} insurer(s)
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {/* Phase 6.4: Insurance mapping button */}
                        {service.billable && insuranceProviders.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenMappings(service)}
                            className="border-2 text-blue-600 hover:bg-blue-50"
                            title="Manage insurer codes"
                          >
                            <Link className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(service)}
                          className="border-2"
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {service.active && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeactivate(service)}
                            className="border-2 text-red-600 hover:bg-red-50"
                            data-testid={`deactivate-service-${service.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No services found</p>
              <p className="text-sm text-slate-500">Add services to your catalogue</p>
            </div>
          )}
        </div>

        {/* Insurance Mapping Modal - Phase 6.4 */}
        <Dialog open={showMappingModal} onOpenChange={setShowMappingModal}>
          <DialogContent className="sm:max-w-2xl border-2 border-slate-200 max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-slate-900 flex items-center gap-2">
                <Link className="w-5 h-5 text-blue-600" />
                Insurer Code Mapping
              </DialogTitle>
            </DialogHeader>
            {mappingService && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-semibold text-slate-900">{mappingService.name}</p>
                  <p className="text-sm text-slate-600">
                    Default price: {formatCurrency(mappingService.default_price)}
                  </p>
                </div>
                
                <p className="text-sm text-slate-600">
                  Set insurer-specific codes and prices for this service. Leave code blank to skip an insurer.
                </p>
                
                <div className="space-y-4">
                  {insurerMappings.map((mapping) => {
                    const provider = insuranceProviders.find(p => p.id === mapping.insurer_id);
                    return (
                      <div key={mapping.insurer_id} className="p-4 border-2 border-slate-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <Building2 className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-slate-900">
                            {provider?.name || mapping.insurer_name}
                          </span>
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono">
                            {provider?.short_code || mapping.insurer_short_code}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Insurer Code</Label>
                            <Input
                              value={mapping.insurer_code}
                              onChange={(e) => handleMappingChange(mapping.insurer_id, 'insurer_code', e.target.value)}
                              placeholder="e.g., CON-001"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Insurer Description</Label>
                            <Input
                              value={mapping.insurer_description}
                              onChange={(e) => handleMappingChange(mapping.insurer_id, 'insurer_description', e.target.value)}
                              placeholder="Optional"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Insurer Price (JMD)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={mapping.insurer_price}
                              onChange={(e) => handleMappingChange(mapping.insurer_id, 'insurer_price', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {insuranceProviders.length === 0 && (
                  <div className="text-center p-4 text-slate-500">
                    <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p>No insurance providers configured</p>
                    <p className="text-sm">Add insurance providers first to set up mappings</p>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowMappingModal(false)}
                    className="border-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveMappings}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={savingMappings}
                  >
                    {savingMappings ? 'Saving...' : 'Save Mappings'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add/Edit Service Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                {editingService ? 'Edit Service' : 'Add Service'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., General Consultation"
                  required
                  data-testid="service-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="service-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <span className="flex items-center gap-2">
                          <cat.icon className="w-4 h-4" />
                          {cat.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="default_price">Default Price (JMD)</Label>
                <Input
                  id="default_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_price}
                  onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="service-price-input"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="billable" className="cursor-pointer">Billable</Label>
                <Switch
                  id="billable"
                  checked={formData.billable}
                  onCheckedChange={(checked) => setFormData({ ...formData, billable: checked })}
                  data-testid="service-billable-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="active" className="cursor-pointer">Active</Label>
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="service-active-switch"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  disabled={saving}
                  data-testid="save-service-btn"
                >
                  {saving ? 'Saving...' : editingService ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ServiceCatalogue;
